import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";

export default function CrudPage({ title, subtitle, endpoint, fields, testPrefix }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = () => Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""]));

  const load = async () => {
    const r = await api.get(`/${endpoint}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const payload = { ...editing };
      delete payload.id;
      if (editing.id) {
        await api.put(`/${endpoint}/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post(`/${endpoint}`, payload);
        toast.success("Eklendi");
      }
      setOpen(false); setEditing(null); load();
    } catch { toast.error("Kaydedilemedi"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/${endpoint}/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid={`${testPrefix}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="label-overline">{subtitle}</div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl tracking-tight">{title}</h1>
        </div>
        <button
          data-testid={`${testPrefix}-add-btn`}
          onClick={() => { setEditing(emptyForm()); setOpen(true); }}
          className="h-11 px-5 bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors flex items-center gap-2 font-medium text-sm"
        >
          <Plus size={16} /> Yeni Ekle
        </button>
      </div>

      <div className="mt-8 grid-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-zinc-50 text-left">
            <tr className="border-b border-zinc-200">
              {fields.map((f) => <th key={f.key} className="px-4 py-3 label-overline font-medium">{f.label}</th>)}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`${testPrefix}-row-${it.id}`}>
                {fields.map((f) => <td key={f.key} className="px-4 py-3">{it[f.key] || "—"}</td>)}
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => { setEditing({ ...it }); setOpen(true); }} className="p-2 hover:bg-zinc-100 rounded" data-testid={`${testPrefix}-edit-${it.id}`}><Edit3 size={13} /></button>
                    <button onClick={() => del(it.id)} className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`${testPrefix}-del-${it.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={fields.length + 1} className="p-8 text-center text-sm text-zinc-500">Kayıt yok</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 rounded-md w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-200 flex justify-between items-center">
              <h2 className="font-display font-bold text-xl">{editing.id ? "Düzenle" : "Yeni Kayıt"}</h2>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-zinc-100 rounded"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-3">
              {fields.map((f) => (
                f.type === "select" ? (
                  <label key={f.key} className="block">
                    <div className="text-xs text-zinc-600 mb-1">{f.label}</div>
                    <select value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="w-full h-10 px-3 border border-zinc-200 rounded-md text-sm bg-white" data-testid={`${testPrefix}-field-${f.key}`}>
                      {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                ) : (
                  <label key={f.key} className="block">
                    <div className="text-xs text-zinc-600 mb-1">{f.label}</div>
                    <input value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="w-full h-10 px-3 border border-zinc-200 rounded-md text-sm" data-testid={`${testPrefix}-field-${f.key}`} />
                  </label>
                )
              ))}
            </div>
            <div className="p-6 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="h-10 px-4 text-sm border border-zinc-200 rounded-md">İptal</button>
              <button onClick={save} className="h-10 px-6 text-sm bg-zinc-950 text-white rounded-md font-medium" data-testid={`${testPrefix}-save-btn`}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
